import { createContext, ReactNode, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { SWRConfig, SWRConfiguration } from 'swr'

import { useAuthStore } from '@/feature/auth'

import { routePaths, unRestrictedRoutes } from '@/lib/core/constants/routes'

export const RefreshContext = createContext<boolean>(false)

type SessionProps = {
  children: ReactNode
}

export const Session = ({ children }: SessionProps) => {
  const authStore = useAuthStore()
  const router = useRouter()

  const cacheMap = useMemo(
    (): SWRConfiguration['provider'] => (m) => {
      let originalData = []
      if (m instanceof Map) {
        // @ts-expect-error There is no good way to handle this typing
        originalData = m.entries()
      } else {
        for (const key of m.keys()) {
          const data = m.get(key)
          originalData.push([key, data])
        }
      }
      return new Map(originalData)
    },
    [authStore.accessToken],
  )

  useEffect(() => {
    if (typeof window === 'undefined' || authStore.isLoading || authStore.isInitializing) return

    if (!authStore.isAuthenticated && !unRestrictedRoutes.includes(router.pathname)) {
      const loginUrl = new URL(routePaths.login, window.location.origin)
      loginUrl.searchParams.append('redirect', window.location.href)
      router.push(loginUrl.toString(), undefined, { shallow: false })
    } else if (authStore.isAuthenticated && router.pathname === routePaths.login) {
      if (router.query.redirect) {
        router.push(router.query.redirect as string, undefined, { shallow: false })
      } else {
        router.push(routePaths.home, undefined, { shallow: false })
      }
    }
  }, [
    authStore.isAuthenticated,
    authStore.isLoading,
    authStore.isInitializing,
    router.pathname,
    router,
  ])

  return (
    <SWRConfig value={{ provider: cacheMap }}>
      {authStore.isInitializing ? null : children}
    </SWRConfig>
  )
}
